import * as React from 'react';
import _ from 'lodash';
import Helmet from 'react-helmet';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { IUpload, IExperimentList, ValidationState } from '@models';
import routes from '@configs/routes.json';
import config from '@configs/config.json';
import { RequestStatus } from '@store/types';
import { IApplicationState } from '@store';
import { HOCLoader, Message } from '@components';
import UploadComponent from './Upload/Upload';
import LocaleLinks from '../locale/LocaleLinks';
import NewExperimentList from './NewExperimentList/NewExperimentList';

import './Upload.scss';
import UploaderVersionBanner from './UploaderVersionBanner/UploaderVersionBanner';

export interface IUploadStateProps {
  experimentLists: IExperimentList[];
  uploads: IUpload[];
  uploadStatus: RequestStatus;
}

export interface IUploadDispatchProps {
  getUploads: () => void;
  experimentListSelected: (id: string, file: File) => void;
  cancel: (upload: IUpload) => void;
  remove: (upload: IUpload) => void;
  validateExperimentsList: (id: string) => void;
  discardExperimentList: (id: string) => void;
  startUpload: (id: string) => void;
}

export interface ChromeFile extends File {
  webkitRelativePath: string;
}

const Upload: React.FC<IUploadStateProps & IUploadDispatchProps> = ({
  experimentLists,
  uploads,
  uploadStatus,
  cancel,
  experimentListSelected,
  getUploads,
  validateExperimentsList,
  discardExperimentList,
  startUpload: uploadExperimentList,
  remove,
}) => {
  const { conf, activeProjectId } = useSelector((state: IApplicationState) => ({
    conf: state.projectPicker.config,
    activeProjectId: state.projectPicker.activeProjectId,
  }));

  React.useEffect(() => {
    const handleRequest = () => {
      if (conf !== undefined && activeProjectId !== null) {
        getUploads();
      }
    };
    handleRequest();
    const id = setInterval(handleRequest, config.upload.uploadPollingInterval);
    return () => clearInterval(id);
  }, [conf, activeProjectId]);

  const { t } = useTranslation();
  const [requestStatus, setRequestStatus] = React.useState<RequestStatus>(
    RequestStatus.inProgress
  );
  let inputRef: any = null;
  let filePickerInputRef: any = null;

  React.useEffect(() => {
    if (inputRef) {
      inputRef.webkitdirectory = true;
    }
  }, [inputRef]);

  React.useEffect(() => {
    if (uploadStatus === RequestStatus.inProgress) {
      setRequestStatus(RequestStatus.inProgress);
    } else if (
      uploadStatus === RequestStatus.completed ||
      experimentLists.length > 0
    ) {
      setRequestStatus(uploadStatus);
    }
  }, [uploads, experimentLists]);

  const onSetExperimentsFile = (fileList: FileList | null) => {
    if (!fileList?.length) {
      return;
    }

    const file: File = fileList[0];
    const id = _.uniqueId();
    experimentListSelected(id, file);
    validateExperimentsList(id);
    filePickerInputRef.value = null;
  };

  const disableSelectFolder: boolean = !!_.find(
    experimentLists,
    (ex) => ex.status === ValidationState.inProgress
  );

  const onRemoveExperimentList = (id: string) => {
    filePickerInputRef.value = null;
    discardExperimentList(id);
  };

  const EmptyUploads = () => (
    <React.Fragment>
      <hr className='mb-0' />
      <Message type='empty' title={t('upload_emptyListTitle')}>
        <div className='fcolor--text-label font-size-14'>
          <LocaleLinks
            localeKey='upload_emptyListDescription'
            links={[{ name: 'emptyUploads', route: '/help' }]}
          />
        </div>
      </Message>
    </React.Fragment>
  );

  const isEmptyData = () => {
    let isEmpty = true;
    if (
      (uploadStatus === RequestStatus.completed && uploads.length > 0) ||
      experimentLists.length > 0 ||
      uploadStatus === RequestStatus.inProgress
    ) {
      isEmpty = false;
    }
    return isEmpty;
  };

  return (
    <div className='upload'>
      <div>
        <UploaderVersionBanner />
      </div>
      <div className='page-header-1'>
        <Helmet>
          <title>{t('upload_title')}</title>
        </Helmet>
        <h1>{t('upload_heading')}</h1>
      </div>
      <hr />
      <form>
        {conf !== undefined ? (
          <div className='clearfix mb-2'>
            <div className='float-left'>
              <input
                type='file'
                ref={(i) => (filePickerInputRef = i)}
                onChange={(e) => onSetExperimentsFile(e.target.files)}
                className='hidden-file-input'
                name='upload-experiments-file'
                id='upload-experiments-file'
                disabled={disableSelectFolder}
                accept={`.${conf.experimentFormat}`}
              />
              <label
                htmlFor='upload-experiments-file'
                className={`btn btn-sm btn-light mb-0 ${
                  disableSelectFolder && 'disabled'
                }`.trim()}
              >
                {t('upload_pickFile', { uppercase: true })}
              </label>
            </div>
            <div className='float-right font-size-13 pt-1 download-links'>
              <a
                className='download-link'
                href={routes.api.file.get.replace(
                  ':fileName',
                  conf.experimentFilename
                )}
              >
                <i className='fas fa-arrow-circle-down fcolor--primary-brand mr-1'></i>
                {t('upload_downloadExperimentListTemplate')}
              </a>

              <NavLink className='download-link' to={routes.app.help}>
                <i className='fas fa-arrow-circle-right fcolor--primary-brand mr-1'></i>
                {t('upload_getUploadAssistantApp')}
              </NavLink>
            </div>
          </div>
        ) : (
          <></>
        )}
      </form>
      <HOCLoader
        isEmpty={isEmptyData()}
        status={requestStatus}
        customEmpty={<EmptyUploads />}
      >
        {_(experimentLists)
          .orderBy('id', 'desc')
          .map((experimentList) => (
            <NewExperimentList
              key={experimentList.id}
              file={experimentList.file}
              status={experimentList.status}
              errors={experimentList.errors}
              experiment={experimentList.experiment}
              removeExperimentList={() =>
                onRemoveExperimentList(experimentList.id)
              }
              upload={() => uploadExperimentList(experimentList.id)}
              startUploadStatus={experimentList.startUploadStatus}
              hasUploads={uploads.length !== 0}
            />
          ))
          .value()}
        <div>
          {_(uploads)
            .orderBy((q) => q.uploadStartDate, 'desc')
            .map((upload) => (
              <UploadComponent
                key={upload.id}
                flowCellName={upload.flowCellName}
                path={upload.path}
                status={upload.status}
                uploadProgress={upload.uploadProgress}
                uploadStartDate={upload.uploadStartDate}
                uploadedBy={upload.uploadedBy}
                cancel={() => cancel(upload)}
                remove={() => remove(upload)}
                experimentListName={upload.experimentListName}
                message={upload.error || upload.message}
                experiment={upload.experiment}
                id={upload.id}
                transmitStarted={upload.transmitStarted}
                transmitEnded={upload.transmitEnded}
                isInFinalStage={upload.isInFinalStage}
              />
            ))
            .value()}
        </div>
      </HOCLoader>
    </div>
  );
};

export default Upload;
